import { type AxiosInstance, isAxiosError } from "axios";
import { dayjs } from "@/core/utils/date";
import {
  AI_SPREADSHEET_OPERATION_STATUS,
  AiSpreadsheetOperation,
  type AiSpreadsheetOperationStatus,
} from "@/payroll/ai-script/types";
import { persistOperation, scopeKey, setSnapshot } from "./store";

const POLL_DELAYS_MS = [400, 700, 1_200, 2_000, 3_500, 5_000];

const MAX_POLL_DURATION_MS = 5 * 60 * 1_000;

const WORKBOOK_OPERATION_PENDING_ERROR = "workbook_operation_pending";

const ACTIVE_OPERATION_STATUSES = new Set<AiSpreadsheetOperationStatus>([
  AI_SPREADSHEET_OPERATION_STATUS.queued,
  AI_SPREADSHEET_OPERATION_STATUS.running,
]);

const OPERATION_STATUSES = new Set<AiSpreadsheetOperationStatus>(
  Object.values(AI_SPREADSHEET_OPERATION_STATUS),
);

export const getWorkbookOperationPath = (
  businessId: string,
  runId: string,
  operationId: string,
) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/operations/${operationId}`;

export const wait = (delay: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Workbook polling stopped", "AbortError"));
      return;
    }

    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      reject(new DOMException("Workbook polling stopped", "AbortError"));
    };
    const timeout = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    signal.addEventListener("abort", onAbort, { once: true });
  });

const isOperationStatus = (
  value: unknown,
): value is AiSpreadsheetOperationStatus =>
  typeof value === "string" &&
  OPERATION_STATUSES.has(value as AiSpreadsheetOperationStatus);

export const isOperation = (
  value: unknown,
): value is AiSpreadsheetOperation => {
  if (!value || typeof value !== "object") return false;
  const operation = value as Partial<AiSpreadsheetOperation>;
  return (
    typeof operation.id === "string" &&
    typeof operation.operation_type === "string" &&
    isOperationStatus(operation.status)
  );
};

export const operationFromConflict = (error: unknown) => {
  if (!isAxiosError(error) || error.response?.status !== 409) return null;
  const data = error.response.data as {
    error?: string;
    operation?: unknown;
  };
  return data.error === WORKBOOK_OPERATION_PENDING_ERROR &&
    isOperation(data.operation)
    ? data.operation
    : null;
};

export const pollOperation = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  initialOperation: AiSpreadsheetOperation,
  signal: AbortSignal,
) => {
  const key = scopeKey(businessId, runId);
  let operation = initialOperation;
  let attempt = 0;
  let transientFailures = 0;
  const startedAt = dayjs();

  while (ACTIVE_OPERATION_STATUSES.has(operation.status)) {
    if (dayjs().diff(startedAt, "milliseconds") >= MAX_POLL_DURATION_MS) {
      throw new Error(
        "Grid recalculation is taking longer than expected. You can retry from this browser to keep checking it.",
      );
    }

    await wait(
      POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)],
      signal,
    );
    attempt += 1;

    try {
      const response = await api.get<{ operation: AiSpreadsheetOperation }>(
        getWorkbookOperationPath(businessId, runId, operation.id),
        { signal },
      );
      if (!isOperation(response.data.operation)) {
        throw new Error("The Grid operation response was not recognized.");
      }
      operation = response.data.operation;
      transientFailures = 0;
      setSnapshot(key, {
        operation,
        isPending: true,
        errorMessage: null,
        retry: null,
      });
      persistOperation(businessId, runId, operation);
    } catch (error) {
      if (signal.aborted) throw error;
      transientFailures += 1;
      if (transientFailures >= 5) throw error;
    }
  }

  return operation;
};

export class WorkbookOperationFailedError extends Error {
  constructor(public operation: AiSpreadsheetOperation) {
    super(operation.error?.message ?? "Grid recalculation failed.");
    this.name = "WorkbookOperationFailedError";
  }
}

export class WorkbookOperationPollingError extends Error {
  constructor(
    public operation: AiSpreadsheetOperation,
    message: string,
  ) {
    super(message);
    this.name = "WorkbookOperationPollingError";
  }
}
